import React, { useState, useEffect } from 'react';
import Login from "./Login";
import Header from "./Header";
import Body from "./Body";
import Footer from "./Footer";
import DagEditor from "./DAG";
import Telemetric from "./Telemetric";
import LogTable from "./LogTable";
import { getDomain, getUser, getAccount, setConfig, getConfig, getClientId } from "./Utils";
import { loadUpdateTime, getMemory, configCorp, flowCorp, loadContexts, callAgent, callTool, run, move } from "./Api.js";

import "./App.css";

const App = () => {

  const [chatChain, setChatChain] = useState([]);
  const [inputText, setInputText] = useState("");  
  const [flow, setFlow] = useState({});
  const [numberOfStates, setNumberOfStates] = useState(0);
  const [cache, setCache] = useState({});
  const [botMsg, setBotMsg] = useState(null);
  const [state, setState] = useState(null);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(null);
  const [updateState, setUpdateState] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [mode, setMode] = useState("Report");
  const [queryParams, setQueryParams] = useState({});

  const scrollToBottom = () => {
    window.scrollTo({
        top: document.body.scrollHeight,
        behavior: "smooth"
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [botMsg, message]);

  function createCaseInsensitiveCache(initial = {}) {
    const store = {};

    // Copia as chaves normalizadas
    for (const key in initial) {
      if (Object.hasOwn(initial, key)) {
        store[key.toLowerCase()] = initial[key];
      }
    }

    return new Proxy(store, {
      get(target, prop) {
        return target[String(prop).toLowerCase()];
      },
      set(target, prop, value) {
        target[String(prop).toLowerCase()] = value;
        return true;
      },
      has(target, prop) {
        return String(prop).toLowerCase() in target;
      },
      deleteProperty(target, prop) {
        return delete target[String(prop).toLowerCase()];
      }
    });
  }

  const loadMsgs = () => {
    var userMsg = null, rational = null;
    if (cache) {
      const icache = createCaseInsensitiveCache(cache);
      userMsg = icache["PERGUNTA ORIGINAL"] ? icache["PERGUNTA ORIGINAL"] : botMsg ? inputText : "";
      if (userMsg && !botMsg) {
        setBotMsg("Claro! Estamos trabalhando nisso, aguarde alguns segundos... 😊");
      }
      if (icache["COMPLEMENTO"]) {
        userMsg = userMsg.trim();
        if (!userMsg.endsWith(".") && !userMsg.endsWith("?") && !userMsg.endsWith("!"))
          userMsg += "."
        userMsg += " Considerando: '";
        if (Array.isArray(icache["COMPLEMENTO"])) {
          userMsg += icache["COMPLEMENTO"].join("','") + "'"
        } else {
          userMsg += icache["COMPLEMENTO"] + "'"
        }
      }
      rational = icache["RACIONAL DE CÁLCULO"];
    }
    if (!Array.isArray(rational))
      rational = [rational];
    return {userMsg, rational};
  }
  
  const update = () => {
    setUpdateState(!updateState);
  }
  
  const getCurrentState = () => {
    if (state != null) {
      return flow[state];
    }
    return null;
  };

  useEffect(() => {

    if (sessionStorage.getItem('logged') != null) {

      const userId = getUser();

      // Injeta o script Pendo no <head> da página
      const script = document.createElement('script');

      script.innerHTML = `
      (function(apiKey){
        (function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=o._q||[];
        v=['initialize','identify','updateOptions','pageLoad','track'];for(w=0,x=v.length;w<x;++w)(function(m){
            o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);
            y=e.createElement(n);y.async=!0;y.src='https://cdn.pendo.io/agent/static/'+apiKey+'/pendo.js';
            z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');

        pendo.initialize({
          visitor: {
            id: '${userId}'
          },
          account: {
            id: '${userId}'
          }
        });
      })('bb4a2d2b-900d-4a1f-4306-00d26bbcd6cd');
    `;
      document.head.appendChild(script);

      configCorp(getAccount()).then(cfg=>{
        setConfig(cfg);
        getMemory().then(memory=>{
          sessionStorage.setItem('memory', JSON.stringify(memory));
        }); 
        loadUpdateTime().then(time=>{
          if (time?.lastUpdate) {
            setLastUpdate(new Date(time.lastUpdate).toLocaleString());
          }
          flowCorp(getAccount()).then(flw=>{
            setFlow(flw);
            setNumberOfStates(findMaxPathLength(flw));
            loadContexts();
            if (state == null) {
              for (let i in flw) {
                if (flw[i].start) {
                  setState(i);
                }
              }
            }
          });
        });
      });
    }
    const params = new URLSearchParams(window.location.search);
    const paramsObject = {};
    for (let [key, value] of params.entries()) {
      paramsObject[key] = value;
    }
    setQueryParams(paramsObject);
  }, []);

  useEffect(() => {
    if (state !== null) {
      runFront();
    }
  }, [state]);

  useEffect(() => {
    if (state !== null) {
      moveFront(getCurrentState());
    }
  }, [cache]);

  function findMaxPathLength(graph) {
    if (!graph || typeof graph !== "object") return 0; 
    function dfs(nodeKey, visited) {
        const node = graph[nodeKey]; 
        if (!node) {
            throw new Error(`Nó "${nodeKey}" não existe no grafo!`);
        }
        visited.add(nodeKey);
        let maxLength = 0;       
        if (node.goto && !visited.has(node.goto)) {
            maxLength = Math.max(maxLength, dfs(node.goto, visited));
        }
        if (node.cases && Array.isArray(node.cases)) {
          for (const obj of node.cases) {
            for (const key in obj) {
              const nextNode = obj[key];
              if (!visited.has(nextNode)) {
                  maxLength = Math.max(maxLength, dfs(nextNode, visited));
              }
            }
          }
        } else if (node.cases && typeof node.cases === "object") {
            for (const key in node.cases) {
              const nextNode = node.cases[key];
              if (!visited.has(nextNode)) {
                  maxLength = Math.max(maxLength, dfs(nextNode, visited));
              }
            }
        }
        visited.delete(nodeKey);
        const nodeType = node.type;
        const weight = (nodeType === "#user" || nodeType === "#bot") ? 0 : 1;
        return weight + maxLength;
    }
    const startNode = Object.keys(graph).find(key => graph[key].start);
    if (!startNode) {
        throw new Error("Grafo não contém nó inicial!");
    }   
    return dfs(startNode, new Set());
  }
  
  async function startFront(input) {
    setCount(0);
    runFront(input);
  }

  async function runFront(input) {
    let creds = {
      corporation: getDomain(),
      clientId: getClientId(),
      username: getUser(),
      database: getConfig()?.database
    };
    let curState = getCurrentState();
    let nodeType = curState?.type;
    let data = {
      state: curState,
      input: input,
      chain: chatChain,
      cache: cache,
      log: creds
    }
    if (!loading)
      setLoading(true);
    let result = await run(data);
    if (nodeType !== "#user" && nodeType !== "#bot")
      setCount(count + 1);
    if (result) {
      if (result.botMsg)
        setBotMsg(result.botMsg);
      if (result.cache)
        setCache(result.cache);
      if (result.message)
        setMessage(result.message);
      if (result.chain)
        setChatChain(result.chain);
    }
    if (curState?.type == "#user")
      setLoading(false);
  }

  async function moveFront(curState) {
    let data = {
      state: curState,
      cache: cache
    }
    let newState = await move(data);
    if (newState != null && newState != "null")
      setState(newState)
  }

  const responseGenerate = async (inputText) => {
    setBotMsg(null);
    setMessage(null);
    setMode("Report");
    if (!inputText) 
      return;
    startFront(inputText);
  }


  if (queryParams?.dag) {
    window.document.getElementsByTagName("body")[0].style.display = "block";
    return <DagEditor/>
  }

  if (queryParams?.telemetry)
    return <Telemetric/>

  if (queryParams?.log)
    return <LogTable/>

  if (sessionStorage.getItem('logged') == null)
    return <Login update={update}/>

  const {userMsg, rational} = loadMsgs();
  return (
    <div>
      <Header/>
      <Body userMsg={userMsg} mode={mode} botMsg={botMsg} loading={loading} rational={rational} count={count} numberOfStates={numberOfStates} message={message}/>
      <Footer responseGenerate={responseGenerate} inputText={inputText} setInputText={setInputText} mode={mode} setMode={setMode} lastUpdate={lastUpdate}/>
    </div>
  );
};

export default App;